/** Магазин бонусов: во что превращаются паркоины.
 *
 * Единственный выход монеты — и он должен ощущаться наградой, а не таблицей.
 * Плитки в две колонки, у каждой крупный «номинал» — скидка цифрой, подарок
 * монетной коробкой; покупка отвечает вспышкой искр, звоном и подпрыгнувшим
 * балансом. Это не украшательство: человек копил, и момент траты — вершина
 * всей петли. Молчаливая кнопка «куплено» эту вершину проглатывает.
 *
 * Сама выдача остаётся прежней: скидка ляжет в «Мои бонусы» и применится к
 * заказу, подарок попадёт менеджеру в «кому должны». Магазин — дверь, а не
 * склад.
 */
import { useEffect, useRef, useState } from 'react';
import { request } from '@/shared/api';
import type { CoinsAnswer, MarketItem } from '@/shared/types';
import { Note, Skeleton, useToast } from '@/shared/ui';
import { tap } from '@/shared/tg';
import { count } from '@/shared/plural';
import { CoinIcon } from '@/shared/coin';
import { chime } from '@/shared/sound';

/** Число, которое докатывается до значения, а не телепортируется.
 *
 * Баланс — главная цифра экрана, и когда он меняется, глаз должен успеть
 * увидеть движение: «было 70, стало 20». Мгновенная замена читается как
 * опечатка, а не как трата.
 */
export function useCountUp(value: number): number {
  const [shown, setShown] = useState(value);
  const prev = useRef(value);

  useEffect(() => {
    const from = prev.current;
    prev.current = value;
    if (from === value) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(value);
      return;
    }
    const started = performance.now();
    const length = 550;
    let frame = 0;
    const step = (now: number) => {
      const k = Math.min(1, (now - started) / length);
      const eased = 1 - Math.pow(1 - k, 3);
      setShown(Math.round(from + (value - from) * eased));
      if (k < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return shown;
}

/** Ярус позиции — по её месту в ценах витрины.

    Дешёвое, среднее и дорогое должны отличаться на глаз до чтения цифр: чем
    выше цена, тем больше золота в материале карточки. Ярус считается от
    разброса цен самой витрины, а не от абсолютных порогов — «дорого» у
    каждого магазина своё.
 */
function tierOf(price: number, prices: number[]): 'common' | 'rare' | 'epic' {
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  if (high === low) return 'rare';
  const t = (price - low) / (high - low);
  return t < 0.34 ? 'common' : t < 0.72 ? 'rare' : 'epic';
}

/** Залп монет из точки покупки: одноразовый, живёт меньше секунды.

    Монеты настоящие — тот же CoinIcon, крупные и в разнобой: маленькая
    одинокая искра читалась как сбой, а не как праздник. Размер и дальность
    полёта посчитаны от номера монеты, чтобы залп был россыпью, а не веером
    по линейке. */
function Burst() {
  return (
    <span className="burst" aria-hidden>
      {Array.from({ length: 10 }, (_, index) => (
        <i
          key={index}
          style={
            {
              /* Угол — шагом в 137°, дальность — тремя радиусами, задержка —
                 пятью волнами: взаимно простые шаги дают россыпь-фонтан */
              '--a': `${(index * 137) % 360}deg`,
              '--d': `${56 + (index % 3) * 24}px`,
              '--dl': `${(index % 5) * 45}ms`,
            } as React.CSSProperties
          }
        >
          <CoinIcon size={index % 3 === 0 ? 26 : index % 2 ? 20 : 16} />
        </i>
      ))}
    </span>
  );
}

/** Подарочная коробка — там, где раньше был эмодзи, рисующийся квадратом. */
export function GiftIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <rect x="3.5" y="9" width="17" height="11.5" rx="2" fill="#f2a03d" />
      <rect x="3.5" y="9" width="17" height="3.4" fill="#c97b1f" />
      <rect x="10.4" y="9" width="3.2" height="11.5" fill="#ffd98a" />
      <rect x="2.6" y="6" width="18.8" height="3.6" rx="1.4" fill="#ffc46b" />
      <path
        d="M12 6C10.8 3.6 8.4 2.7 7 3.9c-1.2 1-.8 2.9 1.2 2.9H12Zm0 0c1.2-2.4 3.6-3.3 5-2.1 1.2 1 .8 2.9-1.2 2.9H12Z"
        fill="#e2532c"
      />
    </svg>
  );
}

/** Баланс — первая личная цифра человека и дверь в магазин. */
export function Balance({ amount, onMarket }: { amount: number; onMarket: () => void }) {
  const shown = useCountUp(amount);
  return (
    <button
      type="button"
      onClick={() => {
        tap();
        onMarket();
      }}
      className="balance-card screen-in mt-3 flex w-full items-center gap-3.5 rounded-layer p-4 text-left"
    >
      <span className="coin-spot grid size-12 shrink-0 place-items-center rounded-2xl">
        <CoinIcon size={34} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block text-[26px] leading-tight font-[760] tracking-[-.02em] text-gold-2"
          data-numeric
        >
          {shown}
        </span>
        <span className="block text-xs text-hint">
          {plainCoins(amount)} на счету
        </span>
      </span>
      <span className="shrink-0 rounded-full bg-gold/18 px-3.5 py-2 text-[13px] font-[680] text-gold-2">
        Магазин ›
      </span>
    </button>
  );
}

/** «паркоинов» без числа: число уже стоит рядом крупно. */
function plainCoins(amount: number): string {
  return count(amount, 'паркоин', 'паркоина', 'паркоинов').replace(/^[\d\s]+/, '');
}

/* ------------------------------------------------------------- магазин */

export function MarketScreen({
  /** Покупка меняет кошелёк наверху: опции заказа читают его оттуда */
  onBought,
}: {
  onBought?: () => void;
} = {}) {
  const [data, setData] = useState<CoinsAnswer | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [justBought, setJustBought] = useState(0);
  /* Искры живут после успеха, а не во время запроса: залп — это «получилось»,
     а не «ждём». Номер залпа в ключе перезапускает анимацию на второй покупке */
  const [burst, setBurst] = useState<{ id: string; n: number } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const { toast, show } = useToast();

  const load = () =>
    request<CoinsAnswer>('/api/coins', {})
      .then(setData)
      .catch((error: Error) => setFailure(error.message));

  useEffect(() => {
    void load();
  }, []);

  if (failure) return <Note tone="warn">Не открылось: {failure}</Note>;
  if (!data) return <Skeleton className="h-40" />;

  const buy = async (item: MarketItem) => {
    tap();
    setBusy(item.id);
    try {
      await request('/api/coins/buy', { item_id: item.id });
      chime();
      tap('success');
      setJustBought((n) => n + 1);
      setBurst((was) => ({ id: item.id, n: (was?.n ?? 0) + 1 }));
      window.setTimeout(() => setBurst(null), 750);
      show(`Ваше: ${item.title}. Бонус ждёт в аккаунте.`);
      onBought?.();
      await load();
    } catch {
      show('Не получилось — обновите и попробуйте ещё раз.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Wallet balance={data.balance} celebrate={justBought} />

      <h2 className="mt-5 mb-2 text-lg font-[680] tracking-[-.02em]">Витрина</h2>
      {data.market.length ? (
        <div className="grid grid-cols-2 gap-2.5">
          {data.market.map((item, index) => {
            const affordable = data.balance >= item.price;
            const missing = item.price - data.balance;
            const tier = tierOf(item.price, data.market.map((one) => one.price));
            return (
              <button
                key={item.id}
                type="button"
                disabled={!affordable || busy !== null}
                onClick={() => void buy(item)}
                style={{ animationDelay: `${Math.min(index, 10) * 45}ms` }}
                className={[
                  'market-tile wet-edge animate-rise relative flex flex-col items-center gap-2',
                  'rounded-layer px-3 pt-4 pb-3 text-center',
                  tier,
                  affordable ? 'can-buy' : 'cannot',
                  burst?.id === item.id ? 'celebrate' : '',
                ].join(' ')}
              >
                {burst?.id === item.id && (
                  <>
                    <span key={`ring-${burst.n}`} className="ring-wave" aria-hidden />
                    <Burst key={burst.n} />
                  </>
                )}
                {/* Пьедестал: номинал стоит в тёплом круге света, а не висит
                    в пустоте — из-за этой пустоты плитка и казалась сырой */}
                {/* 88, не 72: номинал «−30%» упирался цифрами в кромку
                    круга, и пьедестал читался как теснота, а не как свет */}
                <span className="tile-pedestal grid size-[88px] place-items-center rounded-full">
                  {item.kind === 'percent' ? (
                    <span className="text-[26px] leading-none font-[800] tracking-[-.03em] text-mint">
                      −{Math.round(item.value ?? 0)}%
                    </span>
                  ) : (
                    <GiftIcon size={44} />
                  )}
                </span>
                <span className="line-clamp-2 min-h-[2.4em] text-[13.5px] leading-snug font-[650]">
                  {item.title}
                </span>
                {/* Цена — кнопкой во всю ширину: у плитки есть явное действие,
                    и оно называет и цену, и глагол */}
                <span
                  className={[
                    'mt-auto flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2.5',
                    'text-[13.5px] font-[700] transition-colors duration-150',
                    affordable
                      ? 'bg-linear-135 from-gold to-gold-2 text-gold-ink accent-glow-gold'
                      : 'bg-glass text-hint',
                  ].join(' ')}
                  data-numeric
                >
                  {affordable ? `Купить за ${item.price} ` : item.price}
                  <CoinIcon size={16} />
                  
                </span>
                {!affordable && (
                  <span className="text-[10.5px] text-hint">не хватает {missing}</span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <Note>Витрина пока пуста — паркоины никуда не денутся, копите.</Note>
      )}

      {data.history.length > 0 && (
        <>
          <h2 className="mt-5 mb-2 text-lg font-[680] tracking-[-.02em]">История</h2>
          <div className="grid gap-1.5">
            {data.history.map((entry, index) => (
              <div
                key={`${entry.at}-${index}`}
                className="glass flex items-center gap-3 rounded-card px-3.5 py-2.5"
              >
                <ReasonIcon reason={entry.reason} spent={entry.amount < 0} />
                <span className="min-w-0 flex-1 truncate text-[13px]">
                  {entry.reason_label}
                  <span className="block text-[10.5px] text-hint/70">{moment(entry.at)}</span>
                </span>
                <span
                  className={`shrink-0 text-[15px] font-[720] ${
                    entry.amount > 0 ? 'text-good' : 'text-hint'
                  }`}
                  data-numeric
                >
                  {entry.amount > 0 ? `+${entry.amount}` : entry.amount}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {toast}
    </>
  );
}

/** Кошелёк над витриной: монета крупно, число с накатом, вспышка на покупку. */
function Wallet({ balance, celebrate }: { balance: number; celebrate: number }) {
  const shown = useCountUp(balance);
  // Вспышка отдельным состоянием, а не пересозданием: пересозданный кошелёк
  // забывал прежнее число, и накат от «было» к «стало» пропадал
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!celebrate) return;
    setFlash(true);
    const timer = window.setTimeout(() => setFlash(false), 750);
    return () => clearTimeout(timer);
  }, [celebrate]);

  return (
    <div
      className={`balance-card screen-in flex items-center gap-3.5 rounded-layer p-4 ${
        flash ? 'just-bought' : ''
      }`}
    >
      <span className="coin-spot grid size-12 shrink-0 place-items-center rounded-2xl">
        <CoinIcon size={34} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block text-[26px] leading-tight font-[760] tracking-[-.02em] text-gold-2"
          data-numeric
        >
          {shown}
        </span>
        <span className="block text-xs text-hint">{plainCoins(balance)} на счету</span>
      </span>
    </div>
  );
}

/** Значок причины: откуда монеты пришли или куда ушли. Свои, не эмодзи. */
function ReasonIcon({ reason, spent }: { reason: string; spent: boolean }) {
  const stroke = spent ? '#8096a6' : '#52d98b';
  const shared = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  const icon = (() => {
    if (reason === 'market')
      return (
        <svg {...shared}>
          <path d="M5 8h14l-1.2 11.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8Z" />
          <path d="M8.5 10V6.5a3.5 3.5 0 0 1 7 0V10" />
        </svg>
      );
    if (reason === 'game')
      return (
        <svg {...shared}>
          <rect x="4" y="4" width="16" height="16" rx="4" />
          <circle cx="9" cy="9" r="1.2" fill={stroke} stroke="none" />
          <circle cx="15" cy="15" r="1.2" fill={stroke} stroke="none" />
          <circle cx="15" cy="9" r="1.2" fill={stroke} stroke="none" />
          <circle cx="9" cy="15" r="1.2" fill={stroke} stroke="none" />
        </svg>
      );
    if (reason.startsWith('referral'))
      return (
        <svg {...shared}>
          <circle cx="9" cy="8.5" r="3" />
          <path d="M3.5 19c.9-2.9 3-4.4 5.5-4.4s4.6 1.5 5.5 4.4" />
          <path d="M16 8h5M18.5 5.5v5" />
        </svg>
      );
    if (reason.startsWith('order'))
      return (
        <svg {...shared}>
          <path d="M7 3.5h10a1 1 0 0 1 1 1V20l-2.5-1.6L13 20l-2.5-1.6L8 20l-2-1.3V4.5a1 1 0 0 1 1-1Z" />
          <path d="M9.5 8.5h5M9.5 12h5" />
        </svg>
      );
    return (
      <svg {...shared}>
        <path d="m12 3.5 2.4 5 5.6.7-4.1 3.8 1.1 5.5L12 15.7l-5 2.8 1.1-5.5L4 9.2l5.6-.7Z" />
      </svg>
    );
  })();

  return (
    <span className={`grid size-9 shrink-0 place-items-center rounded-xl ${spent ? 'bg-glass' : 'bg-good/12'}`}>
      {icon}
    </span>
  );
}

function moment(stamp: string | null): string {
  if (!stamp) return '';
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)} в ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}
